import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAuthContext } from 'src/auth/useAuthContext';
import { getFileUrl } from 'src/utils/getFileUrl';
import FormProvider, {
  RHFTextField,
  RHFUploadBox,
} from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import { useUploadDocuments } from 'src/hooks/useUploadDocuments';
import { getFileIcon } from 'src/sections/@dashboard/profile/components/ProfileFilesTable';

// ----------------------------------------------------------------------

/**
 * Shape of a document already attached to the visit when the modal opens.
 * `id` is the Mongo `_id` from the `documents` collection — sent back to
 * the backend in `documentIdsToRemove` if the user trashes it.
 */
export type VisitDetailsEditInitialDocument = {
  id: string;
  filename: string;
  url: string;
  originalFilename?: string;
};

export type VisitDetailsEditPayload = {
  description?: string;
  documentsToAdd?: {
    filename: string;
    url: string;
    originalFilename?: string;
    uploadToken?: string;
  }[];
  documentIdsToRemove?: string[];
};

type FormValues = {
  description: string;
};

interface VisitDetailsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDescription: string;
  initialDocuments: VisitDetailsEditInitialDocument[];
  onSubmit: (payload: VisitDetailsEditPayload) => Promise<void>;
  /**
   * The visit id — used to route signed-URL requests to the correct
   * per-entity endpoint. Optional for backwards compatibility; when absent
   * the component falls back to opening the raw URL.
   */
  visitId?: string;
  /**
   * When true, the modal's upload helper uses the public upload endpoint
   * (guest visit page — no firebase token). Mirrors the equivalent flag in
   * `useUploadDocuments`.
   */
  usePublicUpload?: boolean;
  /**
   * Guest session id — required when `usePublicUpload` is true so that
   * uploads include the X-Guest-Session header expected by GuestSessionGuard.
   */
  guestSessionId?: string;
}

/**
 * Shared modal for editing the visit description ("Dolegliwości") and
 * attached documents. Used by both the dashboard's `UpcomingVisitCard`
 * (authed) and the guest visit page's `GuestUpcomingVisitCard` (session-
 * gated). The component is API-agnostic — the parent supplies `onSubmit`
 * which calls the right endpoint.
 *
 * UX matches `SearchBookProfile` (the booking step): RHFTextField for
 * description (multiline rows=6, required) + RHFUploadBox + a list of file
 * rows with file icon, name, optional progress bar, and a trash button.
 * Initial documents share the trash affordance with freshly uploaded
 * ones — for initial docs the trash adds the id to `removedDocumentIds`;
 * for fresh uploads the trash removes them from the uploader hook.
 */
export default function VisitDetailsEditModal({
  isOpen,
  onClose,
  initialDescription,
  initialDocuments,
  onSubmit,
  visitId,
  usePublicUpload = false,
  guestSessionId,
}: VisitDetailsEditModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueTranslated } = useTranslatedSnackbar();
  const { isAuthenticated } = useAuthContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);

  const { upload, downloadURLs, error, remove, reset } = useUploadDocuments({
    usePublicEndpoint: usePublicUpload,
    // When uploading as a guest (post-magic-link), pass visitId and guestSessionId
    // so the hook routes to guestVisitApi.uploadVisitPrivate with X-Guest-Session.
    visitId: usePublicUpload ? visitId : undefined,
    guestSessionId: usePublicUpload ? guestSessionId : undefined,
  });

  const schema = useMemo(
    (): Yup.Schema<FormValues> =>
      Yup.object().shape({
        description: Yup.string()
          .trim()
          .min(1, String(t('validation.healthDescriptionRequired')))
          .max(5000)
          .required(String(t('validation.healthDescriptionRequired'))),
      }),
    [t],
  );

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { description: initialDescription },
  });

  const { handleSubmit, reset: resetForm } = methods;

  // Re-seed ONLY when the modal transitions from closed → open. Adding
  // other deps (initialDescription, resetForm, reset) here is a trap —
  // `reset` from useUploadDocuments isn't memoized, so it changes on
  // every parent re-render and this effect would otherwise wipe the
  // user's in-progress edits on every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen) {
      resetForm({ description: initialDescription });
      setRemovedDocumentIds([]);
      reset();
    }
  }, [isOpen]);

  // The uploader hook surfaces an error flag we clear via re-seed; just
  // un-stick the loading state if a file fails.
  useEffect(() => {
    if (error) setIsUploading(false);
  }, [error]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const newFileNames = acceptedFiles.map((file) => file.name);
      const isDuplicate = newFileNames.some((name) =>
        downloadURLs
          .map((f) => f.originalName || f.name.split('_').slice(2).join('_'))
          .includes(name),
      );
      if (isDuplicate) {
        enqueueTranslated('snackbar.attachmentDuplicate', { variant: 'error' });
        return;
      }
      setIsUploading(true);
      await upload(acceptedFiles);
      setIsUploading(false);
    },
    [upload, downloadURLs, enqueueTranslated],
  );

  const handleRemoveInitial = (id: string) => {
    setRemovedDocumentIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleRemoveUploaded = (filename: string) => {
    remove(filename);
  };

  // Visible "kept" initial docs — for trashed ones we hide them from the
  // list immediately and queue the id for the submit payload.
  const visibleInitialDocs = initialDocuments.filter(
    (d) => !removedDocumentIds.includes(d.id),
  );

  // Fresh uploads from this session (only those finished). The hook
  // tracks in-flight ones too — we still render those so the user sees
  // the progress bar.
  const sessionUploads = downloadURLs;

  const openFile = async (url: string, progress: number) => {
    if (progress !== 100 || !url) return;
    const isFullUrl = url.startsWith('http');
    if (isFullUrl) {
      window.open(url, '_blank');
      return;
    }
    if (!isAuthenticated && !usePublicUpload) return;
    if (!visitId) {
      window.open(url, '_blank');
      return;
    }
    try {
      const signedUrl = await getFileUrl(
        usePublicUpload
          ? { kind: 'guestVisitFile', visitId, filename: url, sessionId: guestSessionId }
          : { kind: 'visitFile', visitId, filename: url },
      );
      window.open(signedUrl, '_blank');
    } catch {
      enqueueTranslated('snackbar.fileOpenFailed', { variant: 'error' });
    }
  };

  const onValidSubmit = async (data: FormValues) => {
    const payload: VisitDetailsEditPayload = {};

    const trimmedDescription = data.description.trim();
    if (trimmedDescription !== initialDescription) {
      payload.description = trimmedDescription;
    }

    const finishedUploads = sessionUploads.filter(
      (f) => f.progress === 100 && f.url,
    );
    if (finishedUploads.length > 0) {
      payload.documentsToAdd = finishedUploads.map((f) => ({
        filename: f.name,
        uploadToken: f.uploadToken,
        url: f.url,
        originalFilename: f.originalName,
      }));
    }

    if (removedDocumentIds.length > 0) {
      payload.documentIdsToRemove = removedDocumentIds;
    }

    // No-op submit: just close.
    if (
      payload.description === undefined &&
      !payload.documentsToAdd &&
      !payload.documentIdsToRemove
    ) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : String(t('snackbar.genericError'));
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="tabletSm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {t('visit.detailsEditModalTitle')}
        <Box flex={1} />
        <IconButton aria-label="close" onClick={onClose}>
          <Iconify icon="eva:close-fill" width={24} height={24} />
        </IconButton>
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onValidSubmit)}>
        <DialogContent>
          <Stack sx={{ pt: 1, pb: 1, gap: 3 }}>
            <RHFTextField
              name="description"
              label={String(t('visit.complaintsHeading'))}
              multiline
              rows={6}
              required
              disabled={isSubmitting}
              inputProps={{ 'data-testid': 'visit-description-field' }}
            />

            {(visibleInitialDocs.length > 0 || sessionUploads.length > 0) && (
              <Typography variant="h6">
                {t('visit.attachmentsHeading')}
              </Typography>
            )}

            {(visibleInitialDocs.length > 0 || sessionUploads.length > 0) && (
              <Stack sx={{ gap: 2 }}>
                {visibleInitialDocs.map((doc) => (
                  <Stack
                    key={doc.id}
                    sx={{
                      gap: 1.5,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 2,
                        flex: 1,
                        cursor: doc.url ? 'pointer' : 'default',
                      }}
                      onClick={() => openFile(doc.url, 100)}
                    >
                      <Box
                        sx={{
                          padding: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.background.neutral,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {getFileIcon(doc.originalFilename || doc.filename)}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flex: 1,
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            '&:hover': doc.url
                              ? { textDecoration: 'underline' }
                              : {},
                          }}
                        >
                          {doc.originalFilename ||
                            doc.filename.split('_').slice(2).join('_') ||
                            doc.filename}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={() => handleRemoveInitial(doc.id)}
                      color="error"
                      disabled={isUploading || isSubmitting}
                      aria-label="remove-attachment"
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                ))}
                {sessionUploads.map(
                  ({ name, url, progress, status, originalName }, idx) => (
                    <Stack
                      key={`${name}-${idx}`}
                      sx={{
                        gap: 1.5,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: 2,
                          flex: 1,
                          cursor:
                            progress === 100 && url ? 'pointer' : 'default',
                        }}
                        onClick={() => openFile(url, progress)}
                      >
                        <Box
                          sx={{
                            padding: 1,
                            borderRadius: 1,
                            backgroundColor: theme.palette.background.neutral,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {getFileIcon(originalName || name)}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              '&:hover':
                                progress === 100 && url
                                  ? { textDecoration: 'underline' }
                                  : {},
                            }}
                          >
                            {originalName ||
                              name.split('_').slice(2).join('_') ||
                              name}
                          </Typography>
                          {progress < 100 && (
                            <>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {status || t('search.fileProcessing')}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ width: 1, height: 5 }}
                              />
                            </>
                          )}
                        </Box>
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveUploaded(name)}
                        color="error"
                        disabled={isUploading || isSubmitting}
                        aria-label="remove-upload"
                      >
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </Stack>
                  ),
                )}
              </Stack>
            )}

            <RHFUploadBox
              sx={{
                flex: 1,
                height: 'inherit',
                borderRadius: 1.5,
                width: 1,
                minHeight: 150,
                m: 0,
              }}
              name="files"
              maxSize={52428800}
              onDrop={(files) => handleDrop(files)}
              placeholder={
                <Stack
                  sx={{ gap: 0.5, p: 3, flex: 1 }}
                  alignItems="center"
                >
                  {!isUploading ? (
                    <>
                      <Iconify icon="eva:cloud-upload-fill" width={40} />
                      <Typography variant="body2">
                        {t('quickBooking.uploadFiles')}
                      </Typography>
                      <Typography variant="caption">
                        {t('quickBooking.maxVideoSize')}
                      </Typography>
                    </>
                  ) : null}
                </Stack>
              }
              disabled={isUploading || isSubmitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isSubmitting || isUploading}
          >
            {t('quickBooking.back')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={isSubmitting}
            disabled={isUploading}
            data-testid="visit-details-save"
          >
            {t('visit.detailsEditSaveAction')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
